import {
  SubscriptionOverview,
  CreatePaymentIntentResponse,
  CreateCardWithIntentPayload,
  UpdateBillingAddressPayload,
  BillingInfoPayload,
} from './interfaces';
import { useEffect, useState } from 'react';
import { useFetch, usePost, usePut } from '@selfserve/react-hooks';
import { apiEndpoints, IIndexSignature, isTrustedHostOrigin } from '@selfserve/react-utils';

export const useSubscriptionOverviewRequest = (enabled = true) =>
  useFetch<SubscriptionOverview>(apiEndpoints.subscriptionOverview, undefined, {
    enabled,
  });

export const useCreatePaymentIntent = (amount: number) =>
  usePost<any, CreatePaymentIntentResponse>(
    apiEndpoints.createPaymentIntent.replace(':amount', String(amount))
  );

export const useCreateCardWithIntent = () =>
  usePut<CreateCardWithIntentPayload, any>(
    apiEndpoints.createCardWithIntent
  );

export const useUpdateBillingAddress = () =>
  usePost<UpdateBillingAddressPayload, any>(apiEndpoints.updateBillingAddress);

export const useBillingInfoRequest = () =>
  usePut<BillingInfoPayload, any>(apiEndpoints.billingInfo);

export const useKavaRequest = (options: IIndexSignature<string>) =>
  useFetch<any>(
    apiEndpoints.kava,
    options,
    {
      enabled: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retryOnMount: false,
    },
    true
  );

export const usePurchaseManagerVersionRequest = () =>
  useFetch<any>(apiEndpoints.purchaseManagerVersion, undefined, {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retryOnMount: false,
  });

export const usePackageManagerVersionRequest = () =>
  useFetch<any>(apiEndpoints.packageManagerVersion, undefined, {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retryOnMount: false,
  });

export const useChargebeeWebhookVersionRequest = () =>
  useFetch<any>(apiEndpoints.chargebeeWebhookVersion, undefined, {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retryOnMount: false,
  });

export const usePurchaseManagerErrorsRequest = () =>
  useFetch<any>(apiEndpoints.purchaseManagerErrors, undefined, {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retryOnMount: false,
  });

export const usePackageManagerErrorsRequest = () =>
  useFetch<any>(apiEndpoints.packageManagerErrors, undefined, {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retryOnMount: false,
  });

export const useChargebeeWebhookErrorsRequest = () =>
  useFetch<any>(apiEndpoints.chargebeeWebhookErrors, undefined, {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retryOnMount: false,
  });

export const scannerErrorsRequest = () =>
  useFetch<any>(apiEndpoints.scannerErrors, undefined, {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retryOnMount: false,
  });

export const postMessageToParent = (message: any) => {
  if (window.parent && window.parent.postMessage) {
    window.parent.postMessage(
      typeof message === 'object'?
        {
          source: 'subscription',
          ...message
        }: message, '*');
  }
}

/** request the ks from the embedding window (iframe parent or window opener) so it is never passed in the url
waitForHost - only when the page was loaded by a host that answers the request, otherwise resolves without a ks
  */
export const useKsFromHost = (requestEventId: string, waitForHost: boolean) => {
  const [host] = useState<Window | null>(() =>
    waitForHost ? (window.parent !== window ? window.parent : window.opener) : null
  );
  const [ks, setKs] = useState<string>();
  const [isResolved, setIsResolved] = useState(!host);

  useEffect(() => {
    if (!host) return;

    const handleMessage = (event: MessageEvent) => {
      // only accept the ks from the embedding window and only when it is a trusted origin
      // otherwise any site could embed this page and log the user in with its own ks
      if (event.source !== host || !isTrustedHostOrigin(event.origin)) return;

      const message = event.data;
      if (message && message.action === 'login' && typeof message.ks === 'string') {
        setKs(message.ks);
        setIsResolved(true);
      }
    };
    window.addEventListener('message', handleMessage);

    const requestEvent = { id: requestEventId, version: '1.0.0', payload: {} };
    if (host === window.parent) {
      postMessageToParent({
        id: 'unisphere.services.iframes.emit',
        version: '1.0.0',
        payload: requestEvent,
      });
    } else {
      host.postMessage({ source: 'subscription', ...requestEvent }, '*');
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [host, requestEventId]);

  return { ks, isResolved };
};

export const usePackageByProductFamilyRequest = (productFamily: string) =>
  useFetch<any[]>(apiEndpoints.packageByProductFamily.replace(':productFamily', productFamily), undefined, {
    productFamily,
    enabled: !!productFamily,
  });

export * from './interfaces';
export * from './schemas';
